/**
 * Artefacts Type System
 *
 * Defines types for detected artefacts (code blocks, documents, tables, HTML)
 * that are automatically extracted from AI-generated message content.
 */

/**
 * Supported artefact types that can be detected and displayed
 */
export enum ArtefactType {
  CODE = "code",
  DOCUMENT = "document",
  TABLE = "table",
  HTML = "html",
}

/**
 * Core artefact interface representing detected content
 */
export interface Artefact {
  /** Unique identifier for the artefact */
  id: string;

  /** Type of artefact detected */
  type: ArtefactType;

  /** Human-readable title for the artefact */
  title: string;

  /** The actual content of the artefact */
  content: string;

  /** Programming language (for code blocks) */
  language?: string;

  /** ID of the message this artefact was extracted from */
  messageId: string;

  /** ISO timestamp when artefact was created */
  timestamp: string;

  /** Additional metadata specific to artefact type */
  metadata?: ArtefactMetadata;
}

/**
 * Type-specific metadata for artefacts
 */
export interface ArtefactMetadata {
  /** Number of lines (for code) */
  lineCount?: number;

  /** File extension suggestion (for downloads) */
  fileExtension?: string;

  /** Original detection pattern matched */
  detectionPattern?: string;

  /** Size estimation for display purposes */
  estimatedSize?: string;

  /** Additional properties for future extensions */
  [key: string]: any;
}

/**
 * Artefact detection configuration
 */
export interface ArtefactDetectionConfig {
  /** Minimum lines for code block detection */
  minCodeLines: number;

  /** Minimum characters for document detection */
  minDocumentChars: number;

  /** Enable/disable specific artefact types */
  enabledTypes: ArtefactType[];

  /** Language aliases for code detection */
  languageAliases: Record<string, string>;
}

/**
 * Default detection configuration
 */
export const DEFAULT_DETECTION_CONFIG: ArtefactDetectionConfig = {
  minCodeLines: 3,
  minDocumentChars: 100,
  enabledTypes: [ArtefactType.CODE, ArtefactType.DOCUMENT, ArtefactType.TABLE, ArtefactType.HTML],
  languageAliases: {
    js: "javascript",
    ts: "typescript",
    py: "python",
    sh: "bash",
    yml: "yaml",
  },
};

/**
 * Result of artefact detection process
 */
export interface ArtefactDetectionResult {
  /** Successfully detected artefacts */
  artefacts: Artefact[];

  /** Number of potential matches that didn't meet threshold */
  skippedCount: number;

  /** Any errors during detection (non-fatal) */
  warnings: string[];
}

/**
 * Active artefact tab in the panel
 */
export type ArtefactTab = "citations" | "generated";

/**
 * Artefact panel state for persistence
 */
export interface ArtefactPanelState {
  /** Whether the artefacts panel is visible */
  visible: boolean;

  /** Currently active tab */
  activeTab: ArtefactTab;

  /** Last updated timestamp */
  lastUpdated: string;
}

/**
 * File extension mapping for different languages
 */
export const LANGUAGE_FILE_EXTENSIONS: Record<string, string> = {
  python: "py",
  javascript: "js",
  typescript: "ts",
  html: "html",
  css: "css",
  json: "json",
  yaml: "yml",
  yml: "yml",
  sql: "sql",
  bash: "sh",
  shell: "sh",
  markdown: "md",
  xml: "xml",
  java: "java",
  cpp: "cpp",
  c: "c",
  go: "go",
  rust: "rs",
  php: "php",
  ruby: "rb",
  swift: "swift",
  kotlin: "kt",
  plantuml: "puml",
  mermaid: "mmd",
};

/**
 * Artefact type display metadata
 */
export const ARTEFACT_TYPE_META: Record<
  ArtefactType,
  { icon: string; label: string; color: string }
> = {
  [ArtefactType.CODE]: {
    icon: "solar:code-bold",
    label: "Code",
    color: "text-blue-600",
  },
  [ArtefactType.DOCUMENT]: {
    icon: "solar:document-text-bold",
    label: "Document",
    color: "text-green-600",
  },
  [ArtefactType.TABLE]: {
    icon: "solar:table-bold",
    label: "Table",
    color: "text-purple-600",
  },
  [ArtefactType.HTML]: {
    icon: "solar:widget-bold",
    label: "HTML",
    color: "text-red-600",
  },
};
